import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * The one raised surface: `.surface` (index.css) — a gradient from --card-top
 * to --card, a hairline in --card-border, the mid shadow and an inset top
 * highlight. Token-driven, so it follows both themes and every company colour.
 *
 * This used to stack five classes (`shadcn-card rounded-lg border-0 shadow-xs
 * card-gradient card-highlight`), two of which carried hardcoded cream stops,
 * and 84 call sites restated some of them. The recipe lives in CSS now and the
 * call site says nothing about how a card is painted.
 *
 * Padding is `--card-pad`: 20px on a phone, 24px from iPad portrait. The
 * fixed `p-6` this replaces spent an eighth of a 375px screen on padding.
 */
const Card = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("surface rounded-lg text-card-foreground", className)}
    {...props}
  />
));
Card.displayName = "Card"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col gap-1 p-card pb-3", className)}
    {...props}
  />
));
CardHeader.displayName = "CardHeader"

/**
 * `as` exists for the pages where the card title IS the page title.
 *
 * A CardTitle renders a `<div>`, which is right inside a page that already has
 * an `<h1>` — a card is not a document section. But the auth pages (Login,
 * Forgot/Reset Password, 2FA) are a single centred card and nothing else, so
 * their heading was a div and those pages had NO heading at all: a screen
 * reader landing on the login screen was given a form with no name. Passing
 * `as="h1"` makes the visible title the real one rather than bolting on a
 * second, hidden heading that says the same thing.
 *
 * 17px bold, not the 24px semibold it was: a card's title outranks its body by
 * one rung, not three. Page-level headings are `PageHeader`'s job.
 */
const CardTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { as?: "div" | "h1" | "h2" | "h3" }
>(({ className, as: Tag = "div", ...props }, ref) => (
  <Tag
    ref={ref as React.Ref<never>}
    className={cn("text-body-lg font-bold leading-snug", className)}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-body text-muted-foreground", className)}
    {...props}
  />
));
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-card pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center gap-tight p-card pt-0", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"
export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
}
